import {inject, Injectable} from '@angular/core';
import {BaseService} from "../BaseService";
import {Dashboard} from "../../models/dashboard/Dashboard";
import {DashboardOpeService} from "../ope/dashboardOpe.service";
import {GetAllUserDashboardsCB} from "../../ope/dashboard/GetAllUserDashboardsCB";
import {GetAllUserDashboardWidgetsCB} from "../../ope/dashboard/GetAllUserDashboardWidgetsCB";
import {AppService} from "../app.service";
import {DashboardWidget} from "../../models/dashboard/DashboardWidget";
import {Widget} from "../../models/dashboard/Widget";
import {GetWidgetsCB} from "../../ope/dashboard/GetWidgetsCB";
import {WidgetEnum} from "../../components/widget/common/WidgetEnum";
import {PutDashboardCB} from "../../ope/dashboard/PutDashboardCB";
import {PutDashboardWidgetCB} from "../../ope/dashboard/PutDashboardWidgetCB";
import {DelDashboardWidgetCB} from "../../ope/dashboard/DelDashboardWidgetCB";
import {DelDashboardCB} from "../../ope/dashboard/DelDashboardCB";
import {GridsterItem} from "angular-gridster2";
import {UserMetricGroup} from "../../models/user/Metric/UserMetricGroup";
import {GetAllUserMetricGroupsCB} from "../../ope/user/metric/GetAllUserMetricGroupsCB";
import {UserOpeService} from "../ope/userOpe.service";
import {GlobalModelService} from "../GlobalModel.service";
import {ToastService} from "../toast.service";
import {Partition} from "../../models/partition/Partition";
import { User } from 'src/app/models/user/User';
import {GetVisibleUsersCB} from "../../ope/user/GetVisibleUsersCB";
import {RoleEnum} from "../../enums/user/RoleEnum";

@Injectable({
  providedIn: 'root'
})
export class DashboardService extends BaseService {

  /**
   * List containing the selected gateway-device ID, only one at a time
   */
  selectedDeviceIds: number[] = [];
  /**
   * Variable containing the currently selected partition used to be displayed in the select box
   */
  selectedPartitionIds: number[] = [];
  isChartDrawerOpen!: boolean;
  widgets: Widget[] = [];
  userDashboards: Dashboard[] = [];
  visibleUsers: User[] = [];
  /**
   * User whose dashboards are currently shown. Undefined means the authenticated user:
   * getAllUserDashboards/getAllUserDashboardWidgets fall back to self when no userId is sent.
   */
  impersonatedUserId?: number;
  userMetricGroups: UserMetricGroup[] = [];
  dashboardEditMode!: boolean;
  selectedDashboard!: Dashboard | null;
  selDashboardWidgets: DashboardWidget[] = [];
  selUserMetricGroup!: UserMetricGroup | null;
  fullSelUserMetricGroup!: UserMetricGroup | null;
  partitions: Partition[] = [];
  protected toastService: ToastService = inject(ToastService);
  private dashboardOpeService = inject(DashboardOpeService);
  private userOpeService = inject(UserOpeService);
  private gmService: GlobalModelService = inject(GlobalModelService);
  private appService: AppService = inject(AppService);

  constructor() {
    super();
    this.cleanupServiceFields();
  }

  cleanupServiceFields(): void {

    this.dashboardEditMode = false;
    this.isChartDrawerOpen = false;
    this.widgets = [];
    this.userDashboards = [];
    this.visibleUsers = [];
    this.impersonatedUserId = undefined;
    this.selDashboardWidgets = [];
    this.userMetricGroups = [];
    this.selectedDeviceIds = [];
    this.selectedPartitionIds = [];
    this.selectedDashboard = null;
    this.selUserMetricGroup = null;
    this.fullSelUserMetricGroup = null;
    this.partitions = [];
  }

  /**
   * Charts of the impersonated user, or of the authenticated one when nobody is impersonated.
   */
  async getUserMetricGroups() {
    try {
      let backedUpMetricGroup = this.appService.deepCopy(this.selUserMetricGroup);
      this.selUserMetricGroup = null;
      let cb = new GetAllUserMetricGroupsCB();
      cb.i.userId = this.impersonatedUserId ?? this.gmService.user.id;
      await this.userOpeService.getAllUserMetricGroups(cb);
      cb.o.groups.sort((a, b) => a.name.localeCompare(b.name));
      this.userMetricGroups = [];
      cb.o.groups.forEach(group => {
        let newGroup = new UserMetricGroup();
        newGroup.id = group.id;
        newGroup.name = group.name;
        this.userMetricGroups.push(newGroup);
      });
      if (!!backedUpMetricGroup) {
        let umgToSync = this.userMetricGroups.find(umg => umg.id === backedUpMetricGroup!.id);
        if (!!umgToSync)
          this.selUserMetricGroup = umgToSync;
      }
    } catch (error: any) {
      console.log(error);
    }
  }

  getAvailableWidgets(): Promise<Widget[]> {
    const cb = new GetWidgetsCB();

    return new Promise<Widget[]>((resolve, reject) => {
      this.dashboardOpeService.getWidgets(cb).then((_) => {
        this.widgets = cb.o.widgets.filter(w => w.enabled);
        resolve(this.widgets);
      }, (error) => {
        reject(error);
      });
    })

  }

  /**
   * Dashboards of the impersonated user, or of the authenticated one when nobody is impersonated.
   */
  getUserDashboards(): Promise<Dashboard[]> {
    const cb = new GetAllUserDashboardsCB();
    cb.i.userId = this.impersonatedUserId ?? this.gmService.user.id;

    return new Promise<Dashboard[]>((resolve, reject) => {
      this.dashboardOpeService.getAllUserDashboards(cb)
        .then((_) => {
          this.userDashboards = cb.o.dashboards.sort(
            (a, b) => {
              return a.name.localeCompare(b.name)
            });
          // the select box matches its value by reference, so the selection must point into the new list
          if (!!this.selectedDashboard) {
            const dashboardToSync = this.userDashboards.find(d => d.id === this.selectedDashboard!.id);
            if (!!dashboardToSync) {
              this.selectedDashboard = dashboardToSync;
            } else {
              // deleted or no longer visible: keeping it would leave the select box on a stale value
              this.selectedDashboard = null;
              this.selDashboardWidgets = [];
            }
          }
          resolve(this.userDashboards);
        }, (error) => {
          reject(error);
        });
    })

  }

  /**
   * Switches the impersonated user (undefined = back to the authenticated one) and drops what
   * belonged to the previous one, on both pages: the selection is shared, so coming back to the
   * other page must not show the previous user's list. Reloading is left to whichever page is open,
   * which owns its own "pick the first one, then load it" logic.
   */
  setImpersonatedUser(userId?: number) {
    this.impersonatedUserId = userId;
    this.selectedDashboard = null;
    this.selDashboardWidgets = [];
    this.userDashboards = [];
    this.selUserMetricGroup = null;
    this.fullSelUserMetricGroup = null;
    this.userMetricGroups = [];
  }

  /**
   * Shared by the topbar impersonation select box and the duplicate popup recipients, so the two
   * lists read the same. Arrow function: DevExtreme calls it unbound as a displayExpr.
   */
  visibleUserDisplayExpr = (user: User): string => {
    if (!user)
      return '';

    const name = `${user.firstname} ${user.lastname}`;
    if (user.id === this.gmService.user?.id)
      return `${name} (${this.translate.instant('COMMON.ME')})`;
    return !user.partitionName ? name : `${user.partitionName} | ${name}`;
  };

  /**
   * Users the authenticated one can look at, themselves included.
   */
  getUserImpersonationOptions(showSpinner: boolean = true): Promise<User[]> {
    const cb = new GetVisibleUsersCB();

    return new Promise<User[]>((resolve, reject) => {
      this.userOpeService.getVisibleUsers(cb, showSpinner).then((_) => {
        // enabled is nullable on the wire, User needs it set
        this.visibleUsers = cb.o.users
          .map(u => ({...u, enabled: u.enabled ?? false}))
          // the authenticated user first, then the label's order, otherwise partitions interleave
          .sort((a, b) => Number(b.id === this.gmService.user.id) - Number(a.id === this.gmService.user.id)
            || this.visibleUserDisplayExpr(a).localeCompare(this.visibleUserDisplayExpr(b)));
        // start on the authenticated user, so the select box isn't empty
        this.impersonatedUserId ??= this.gmService.user.id;
        resolve(this.visibleUsers);
      }, (error) => {
        reject(error);
      });
    })

  }

  putDashboard(d: Dashboard): Promise<number> {

    const cb = new PutDashboardCB();
    cb.i.name = d.name;

    if (!!d.id) {
      cb.i.id = d.id;
    }

    return new Promise<number>((resolve, reject) => {
      this.dashboardOpeService.putDashboard(cb).then((_) => {
        resolve(cb.o.id);
      }, (error) => {
        reject(error);
      });
    })

  }

  putDashboardWidget(dId: number, dw: DashboardWidget): Promise<number> {

    const cb = new PutDashboardWidgetCB();

    if (!!dw.id) {
      cb.i.id = dw.id;
    }

    cb.i.dashboardId = dId;
    cb.i.widgetId = dw.widgetId;
    cb.i.name = dw.name;
    cb.i.colsSize = dw.colsSize;
    cb.i.rowsSize = dw.rowsSize;
    cb.i.colsStart = dw.colsStart;
    cb.i.rowsStart = dw.rowsStart;
    cb.i.userConfig = dw.userConfig;
    cb.i.files = dw.files;
    cb.i.metricInstanceIds = dw.metricInstanceIds;

    return new Promise<number>((resolve, reject) => {
      this.dashboardOpeService.putDashboardWidget(cb).then((_) => {
        resolve(cb.o.id);
      }, (error) => {
        reject(error);
      });
    });

  }

  deleteDashboard(id: number): Promise<void> {
    const cb = new DelDashboardCB();
    cb.i.id = id;

    return new Promise<void>((resolve, reject) => {
      this.dashboardOpeService.delDashboard(cb).then((_) => {
        resolve();
      }, (error) => {
        reject(error);
      });
    })
  }

  deleteWidgetFromDashboard(id: number): Promise<void> {

    const cb = new DelDashboardWidgetCB();
    cb.i.id = id;

    return new Promise<void>((resolve, reject) => {
      this.dashboardOpeService.delDashboardWidget(cb).then((_) => {
        resolve();
      }, (error) => {
        reject(error);
      });
    })

  }


  getWidgetsOfSelectedDashboard(): Promise<DashboardWidget[]> {
    this.selDashboardWidgets = [];
    return new Promise((resolve, reject) => {

      if (!!this.selectedDashboard) {

        // the All variant covers the caller's own dashboards too, getDashboardWidgets is
        // self-only and would 403 on an impersonated one
        const cb = new GetAllUserDashboardWidgetsCB();
        cb.i.dashboardId = this.selectedDashboard.id;

        this.dashboardOpeService.getAllUserDashboardWidgets(cb).then((_) => {
          this.selDashboardWidgets = cb.o.dashboardWidgets;
          resolve(this.selDashboardWidgets);
        }).catch((error) => {
          reject(error);
        });

      } else {
        reject('No dashboard selected');
      }

    });
  }

  getWidgetTypeOfWidget(id: number): WidgetEnum | undefined {
    const widget = this.widgets.find(w => w.id === id);
    return widget?.name;
  }

  getWidgetFromId(id: number): Widget | undefined {
    return this.widgets.find(w => w.id === id);

  }

  getWidgetMaxColsAndRows(id: number): { cols: number, rows: number } {
    const widget = this.widgets.find(w => w.id === id);
    if (!widget) {
      return {cols: 0, rows: 0};
    }

    return {cols: widget.maxColsSize, rows: widget.maxRowsSize};
  }

  getWidgetMinColsAndRows(id: number): { cols: number, rows: number } {
    const widget = this.widgets.find(w => w.id === id);
    if (!widget) {
      return {cols: 0, rows: 0};
    }

    return {cols: widget.minColsSize, rows: widget.minRowsSize};
  }

  itemHasChanged(item: GridsterItem) {

    const dwIndex = this.selDashboardWidgets?.findIndex(w => w.id === item.id);
    if (dwIndex === -1) {
      return false;
    }

    let dw = this.selDashboardWidgets[dwIndex];
    if (dw.colsSize !== item.cols || dw.rowsSize !== item.rows || dw.colsStart !== item.x || dw.rowsStart !== item.y) {
      this.selDashboardWidgets[dwIndex] = DashboardWidget.clone(item);
      return true;
    } else {
      return false
    }
  }

  /**
   * Undefined means "not resolved yet": until getUserImpersonationOptions answers, the dashboards on
   * screen are the authenticated user's own, so impersonation must not be reported.
   */
  isImpersonatingUser(): boolean {
    return this.impersonatedUserId !== undefined && this.impersonatedUserId !== this.gmService.user?.id
  }

  /**
   * User whose dashboards are on screen: the impersonated one, or the authenticated one.
   */
  get displayedUserId(): number | undefined {
    return this.impersonatedUserId ?? this.gmService.user?.id;
  }

  /**
   * Only administrators get the recipient selector when sharing: a standard user can duplicate
   * for themselves only (shareDashboard would skip anyone else anyway).
   */
  canShareToOtherUsers(): boolean {
    const roles = this.gmService.user?.roles ?? [];
    return roles.includes(RoleEnum.R_SYSTEM) || roles.includes(RoleEnum.R_PARTITION_ADMIN);
  }
}
